import React, { useEffect, useId } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import PayPalCheckout from 'utils/PayPalCheckout';

/**
 * Renders the Braintree PayPal Checkout SDK button inside an isolated container.
 * Used by PlaceOrderButton in Frictionless Checkout R4 to replace the standard
 * Place Order button while PayPal is the selected payment method.
 *
 * The container ID is generated with React's useId() so it is stable across SSR
 * and unique per instance, avoiding collisions when multiple buttons are mounted
 * on the same page. The button is rendered on mount; on unmount the effect calls
 * PayPalCheckout.cleanupPayPalButton, which clears the internal bookkeeping for
 * this container so a future remount can render again. The iframe itself is
 * removed by React together with the container div.
 *
 * @param {Object} props
 * @param {boolean} [props.isBopis] - True when the order is a BOPIS pickup;
 *   forwarded to PayPalCheckout so the SDK uses the pickup flow.
 * @returns {JSX.Element} A div container that PayPal SDK fills with its button iframe.
 */
function PayPalButton({ isBopis }) {
    // Use React's useId() for SSR-safe unique IDs
    const id = useId();
    // Remove colons to make the ID CSS-selector-safe
    const containerId = `paypal-fc-place-order-button-container-${id.replace(/:/g, '')}`;

    useEffect(() => {
        PayPalCheckout.renderPayPalButton({ containerId, isBopis });

        return () => {
            PayPalCheckout.cleanupPayPalButton({ containerId });
        };
    }, [containerId, isBopis]);

    return <div id={containerId} />;
}

export default wrapFunctionalComponent(PayPalButton, 'PayPalButton');
